import { GraphQLFieldConfig, GraphQLNonNull, GraphQLList } from 'graphql'
import {
  ApplicationAggregationType,
  ApplicationAggregation
} from '../types/application-aggregation'
import { Context } from '../shared'
import {
  ApplicationAggregationArgs,
  applicationAggregationArgs
} from '../types/application-aggregation-args'
import {
  CustomMetadataBuckets,
  CustomMetadataBucketsType
} from '../types/bucket'
import {
  ContrastQueryParameters,
  appendApplicationFiltersToBody
} from '../endpoint-helpers'
import {
  extractBuckets,
  filterGroupsToBuckets,
  mapLabeledBucket,
  mapServerBucket,
  mapTermBucket
} from '../endpoint-helpers/applications'

function mapComplianceBucket({ keycode: id, label: name, count }) {
  return {
    count,
    key: {
      id,
      name
    }
  }
}

const extractTermBuckets = extractBuckets(mapTermBucket)
const extractServerBuckets = extractBuckets(mapServerBucket)
const extractLanguageBuckets = extractBuckets(mapLabeledBucket)
const extractComplianceBuckets = extractBuckets(mapComplianceBucket)
const extractLabeledBuckets = extractBuckets(mapLabeledBucket)
const extractServerEnvironmentBuckets = extractBuckets(mapLabeledBucket)
const extractAppImportanceBuckets = extractBuckets(mapLabeledBucket)

type ApplicationBucket =
  | 'scores'
  | 'servers'
  | 'tags'
  | 'technologies'
  | 'languages'
  | 'compliance'
  | 'open-vuln-severity'
  | 'server-environments'
  | 'app-importance'

export const applicationAggregations: GraphQLFieldConfig<
  any,
  Context,
  ApplicationAggregationArgs
> = {
  description: 'Get the aggregation for a collection of applications',
  type: new GraphQLNonNull(ApplicationAggregationType),
  args: applicationAggregationArgs,
  async resolve(_source, args, context): Promise<ApplicationAggregation> {
    const { organizationID, filters } = args

    const params = new ContrastQueryParameters()
    params.append('expand', 'skip_links')

    const mappedFilters = appendApplicationFiltersToBody(filters)

    const getAggregation = (bucketType: ApplicationBucket) =>
      context.connector.post(
        `${organizationID}/applications/filters/${bucketType}/listing/?${params}`,
        mappedFilters
      )

    const [
      scores,
      servers,
      tags,
      technologies,
      languages,
      compliance,
      openVulnSeverities,
      environment,
      appImportances
    ] = await Promise.all([
      getAggregation('scores').then(extractTermBuckets),
      getAggregation('servers').then(extractServerBuckets),
      getAggregation('tags').then(extractTermBuckets),
      getAggregation('technologies').then(extractTermBuckets),
      getAggregation('languages').then(extractLanguageBuckets),
      getAggregation('compliance').then(extractComplianceBuckets),
      getAggregation('open-vuln-severity').then(extractLabeledBuckets),
      getAggregation('server-environments').then(
        extractServerEnvironmentBuckets
      ),
      getAggregation('app-importance').then(extractAppImportanceBuckets)
    ])

    return {
      scores,
      servers,
      tags,
      technologies,
      languages,
      compliance,
      openVulnSeverities,
      environment,
      appImportances
    }
  }
}

export const customApplicationAggregations: GraphQLFieldConfig<
  any,
  Context,
  ApplicationAggregationArgs
> = {
  description:
    'Get the aggregations of custom metadata values for a collection of applications',
  type: new GraphQLNonNull(new GraphQLList(CustomMetadataBucketsType)),
  args: applicationAggregationArgs,
  async resolve(_source, args, context): Promise<CustomMetadataBuckets[]> {
    const { organizationID, filters } = args

    const params = new ContrastQueryParameters()
    params.append('expand', 'skip_links')

    const mappedFilters = appendApplicationFiltersToBody(filters)

    const { filters: metadataFilters } = await context.connector.post(
      `/${organizationID}/metadata/application/filters?${params}`,
      mappedFilters
    )

    return filterGroupsToBuckets(metadataFilters)
  }
}
